/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import {
  IconButton, Typography, Button, CircularProgress, Box, TextField,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { LeaseDetail, DocumentType } from './types';
import DocumentCard from './DocumentCard';
import UploadDialog from './UploadDialog';

interface LeaseDetailSideSheetProps {
  leaseId: number;
  isClosing: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export default function LeaseDetailSideSheet({
  leaseId,
  isClosing,
  onClose,
  onRefresh,
}: LeaseDetailSideSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [amendmentDialogOpen, setAmendmentDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);

  // Inline editing for tenant name
  const [isEditingTenantName, setIsEditingTenantName] = useState(false);
  const [editTenantNameValue, setEditTenantNameValue] = useState('');

  // Inline editing for unit/suite
  const [isEditingUnitSuite, setIsEditingUnitSuite] = useState(false);
  const [editUnitSuiteValue, setEditUnitSuiteValue] = useState('');

  // Fetch lease detail
  const { data: lease, isLoading } = useQuery(
    ['get/leases', leaseId],
    async () => axios({
      url: `api/leases/${leaseId}/`,
      method: 'get',
    }),
    {
      enabled: Boolean(leaseId),
      select: (res: AxiosResponse) => res.data.data as LeaseDetail,
      refetchInterval: (data) => {
        const anyProcessing = data?.is_processing || data?.documents?.some((d) => !d.is_processed);
        return anyProcessing ? 5000 : false;
      },
    },
  );

  // Add amendment mutation
  const { mutate: addAmendment, isLoading: isAddingAmendment } = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append('pdf', file);
      return axios({
        url: `api/leases/${leaseId}/add_amendment/`,
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        await onRefresh();
        enqueueSnackbar('Amendment uploaded and processing started');
      },
      onError: () => {
        enqueueSnackbar('Failed to upload amendment', { variant: 'error' });
      },
    },
  );

  // Replace lease mutation
  const { mutate: replaceLease, isLoading: isReplacing } = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append('pdf', file);
      return axios({
        url: `api/leases/${leaseId}/replace_lease/`,
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        await onRefresh();
        enqueueSnackbar('Base lease replaced and re-processing started');
      },
      onError: () => {
        enqueueSnackbar('Failed to replace lease', { variant: 'error' });
      },
    },
  );

  // Update tenant name mutation
  const { mutate: updateTenantName, isLoading: isUpdatingTenantName } = useMutation(
    async ({ id, tenantName }: { id: number; tenantName: string }) => axios({
      url: `api/leases/${id}/`,
      method: 'patch',
      data: { tenant_name: tenantName },
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        await queryClient.invalidateQueries('get/leases');
        await queryClient.invalidateQueries('get/options-monitor');
        enqueueSnackbar('Tenant name updated');
        setIsEditingTenantName(false);
      },
      onError: () => {
        enqueueSnackbar('Failed to update tenant name', { variant: 'error' });
        setIsEditingTenantName(false);
      },
    },
  );

  // Update unit/suite mutation
  const { mutate: updateUnitSuite, isLoading: isUpdatingUnitSuite } = useMutation(
    async ({ id, unitSuite }: { id: number; unitSuite: string }) => axios({
      url: `api/leases/${id}/`,
      method: 'patch',
      data: { unit_suite: unitSuite },
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        await queryClient.invalidateQueries('get/leases');
        await queryClient.invalidateQueries('get/options-monitor');
        enqueueSnackbar('Suite updated');
        setIsEditingUnitSuite(false);
      },
      onError: () => {
        enqueueSnackbar('Failed to update suite', { variant: 'error' });
        setIsEditingUnitSuite(false);
      },
    },
  );

  // Update document type mutation
  const { mutate: updateDocType } = useMutation(
    async ({ documentId, documentType }: { documentId: number; documentType: DocumentType }) => axios({
      url: `api/leases/${leaseId}/update_document/`,
      method: 'patch',
      data: { document_id: documentId, document_type: documentType },
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        enqueueSnackbar('Document updated');
      },
      onError: () => {
        enqueueSnackbar('Failed to update document', { variant: 'error' });
      },
    },
  );

  // Remove document mutation
  const { mutate: removeDocument } = useMutation(
    async (documentId: number) => axios({
      url: `api/leases/${leaseId}/remove_document/`,
      method: 'delete',
      data: { document_id: documentId },
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['get/leases', leaseId]);
        enqueueSnackbar('Document removed');
      },
      onError: () => {
        enqueueSnackbar('Failed to remove document', { variant: 'error' });
      },
    },
  );

  return (
    <div className={`leases-detail-sidesheet${isClosing ? ' closing' : ''}`}>
      {/* Header */}
      <div className='sidesheet-header'>
        <div className='header-top-row'>
          <div style={{ flex: 1 }}>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {isEditingTenantName ? (
                  <Box display='flex' alignItems='center' gap={0.5} mb={0.5}>
                    <TextField
                      size='small'
                      value={editTenantNameValue}
                      onChange={(e): void => { setEditTenantNameValue(e.target.value); }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                          updateTenantName({ id: leaseId, tenantName: editTenantNameValue });
                        } else if (e.key === 'Escape') {
                          setIsEditingTenantName(false);
                        }
                      }}
                      autoFocus
                      placeholder='Enter tenant name'
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '16px', fontWeight: 600, padding: '4px 8px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                      }}
                    />
                    <IconButton
                      size='small'
                      onClick={(): void => { updateTenantName({ id: leaseId, tenantName: editTenantNameValue }); }}
                      disabled={isUpdatingTenantName}
                      sx={{ color: '#00CFA1' }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={(): void => { setIsEditingTenantName(false); }}
                      sx={{ color: '#6B7280' }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <h2
                    className='header-title'
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    onClick={(): void => {
                      setIsEditingTenantName(true);
                      setEditTenantNameValue(lease?.tenant_name || '');
                    }}
                    aria-hidden='true'
                  >
                    {lease?.tenant_name || 'Unnamed Tenant'}
                    <EditIcon sx={{ fontSize: 14, color: '#9CA3AF', opacity: 0.6 }} />
                  </h2>
                )}
                <p className='header-subtitle'>{lease?.building_name || ''}</p>
                <p className='header-agreement'>{lease?.lease_agreement_date || ''}</p>
              </>
            )}
          </div>
          <IconButton onClick={onClose} size='small'>
            <ClearIcon />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      <div className='sidesheet-content'>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </div>
        ) : lease ? (
          <>
            {/* Info Grid */}
            <div className='info-grid'>
              <div className='info-item'>
                <span className='info-label'>Current Expiration</span>
                <span className='info-value'>{formatDate(lease.lease_expiration_date)}</span>
                {lease.expiration_source && (
                  <span className='info-source'>
                    Derived from: {lease.expiration_source}
                  </span>
                )}
              </div>
              <div className='info-item'>
                <span className='info-label'>Amendments</span>
                <span className='info-value'>{lease.amendment_count}</span>
              </div>
              <div className='info-item'>
                <span className='info-label'>Suite</span>
                {isEditingUnitSuite ? (
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <TextField
                      size='small'
                      value={editUnitSuiteValue}
                      onChange={(e): void => { setEditUnitSuiteValue(e.target.value); }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                          updateUnitSuite({ id: leaseId, unitSuite: editUnitSuiteValue });
                        } else if (e.key === 'Escape') {
                          setIsEditingUnitSuite(false);
                        }
                      }}
                      onBlur={(): void => { updateUnitSuite({ id: leaseId, unitSuite: editUnitSuiteValue }); }}
                      autoFocus
                      placeholder='Enter suite'
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-input': { fontSize: '13px', padding: '4px 8px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                      }}
                    />
                    <IconButton
                      size='small'
                      onClick={(): void => { updateUnitSuite({ id: leaseId, unitSuite: editUnitSuiteValue }); }}
                      disabled={isUpdatingUnitSuite}
                      sx={{ color: '#00CFA1' }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={(): void => { setIsEditingUnitSuite(false); }}
                      sx={{ color: '#6B7280' }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <span
                    className='info-value'
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={(): void => { setIsEditingUnitSuite(true); setEditUnitSuiteValue(lease.unit_suite || ''); }}
                    aria-hidden='true'
                  >
                    {lease.unit_suite || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Click to add</span>}
                    <EditIcon sx={{ fontSize: 14, color: '#9CA3AF', opacity: 0.6 }} />
                  </span>
                )}
              </div>
              <div className='info-item'>
                <span className='info-label'>Has Options</span>
                <span className='info-value'>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: lease.has_options ? '#E8F5E9' : '#F3F4F6',
                      color: lease.has_options ? '#2E7D32' : '#6B7280',
                    }}
                  >
                    {lease.has_options ? 'Yes' : 'No'}
                  </span>
                </span>
              </div>
            </div>

            {/* Documents Section */}
            <div className='documents-section'>
              <div className='section-header'>
                <span className='section-title'>Documents</span>
              </div>

              {lease.documents?.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  leaseId={leaseId}
                  isUploading={isReplacing && doc.document_type === 'base_lease'}
                  onTypeChange={(documentId, newType) => {
                    updateDocType({ documentId, documentType: newType });
                  }}
                  onDelete={(documentId) => {
                    removeDocument(documentId);
                  }}
                  canDelete={(lease.documents?.length || 0) > 1}
                />
              ))}

              {isAddingAmendment && (
                <div
                  style={{
                    position: 'relative',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '8px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <CircularProgress size={16} sx={{ color: '#00CFA1', flexShrink: 0 }} />
                  <div>
                    <span
                      style={{
                        backgroundColor: '#F0FDF4',
                        color: '#15803D',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        marginBottom: '3px',
                      }}
                    >
                      Amendment
                    </span>
                    <Typography variant='body2' style={{ color: '#9CA3AF', fontSize: '12px' }}>
                      Uploading...
                    </Typography>
                  </div>
                </div>
              )}

              {/* Action buttons below documents */}
              <div className='documents-actions'>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={(): void => { setAmendmentDialogOpen(true); }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    color: '#374151',
                    borderColor: '#D1D5DB',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Add Amendment
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<SwapHorizIcon />}
                  onClick={(): void => { setReplaceDialogOpen(true); }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    color: '#374151',
                    borderColor: '#D1D5DB',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Replace Lease
                </Button>
                {(lease.documents?.length || 0) > 1 && (
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={(): void => {
                      window.location.href = `${process.env.REACT_APP_BASE_URL}/api/leases/${leaseId}/download_all_documents/`;
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      color: '#374151',
                      borderColor: '#D1D5DB',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      '&:hover': {
                        borderColor: '#9CA3AF',
                        backgroundColor: '#F9FAFB',
                      },
                    }}
                  >
                    Combine &amp; Download
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <Typography style={{ color: '#9CA3AF', textAlign: 'center', padding: '40px' }}>
            Lease not found
          </Typography>
        )}
      </div>

      {/* Upload Dialogs */}
      <UploadDialog
        open={amendmentDialogOpen}
        onClose={(): void => { setAmendmentDialogOpen(false); }}
        onUpload={(file: File): void => { addAmendment(file); }}
        title='Upload Amendment'
        subtitle='This file will be classified as an Amendment.'
      />

      <UploadDialog
        open={replaceDialogOpen}
        onClose={(): void => { setReplaceDialogOpen(false); }}
        onUpload={(file: File): void => { replaceLease(file); }}
        title='Replace Base Lease'
        subtitle='This will replace the existing base lease document and re-process the entire lease.'
      />
    </div>
  );
}
